import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

interface PendingInvitation {
  id: string;
  team_id: string;
  invited_email: string;
  role: string;
  expires_at: string;
  teams: {
    id: string;
    name: string;
    company_name: string;
  };
}

export const usePendingInvitations = () => {
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchInvitations = async () => {
    if (!user?.email) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('team_invitations')
        .select(`
          *,
          teams (
            id,
            name,
            company_name
          )
        `)
        .eq('invited_email', user.email)
        .is('accepted_at', null)
        .gt('expires_at', new Date().toISOString());

      if (error) throw error;
      setInvitations(data || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async (invitationId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('accept-team-invitation', {
        body: { invitationId },
      });

      if (error) throw error;
      
      // Refresh invitations after accepting
      await fetchInvitations();
      return true;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      return false;
    }
  };

  const declineInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;
      
      // Refresh invitations after declining
      await fetchInvitations();
      return true;
    } catch (error) {
      console.error('Error declining invitation:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [user?.email]);

  return {
    invitations,
    loading,
    acceptInvitation,
    declineInvitation,
    refetch: fetchInvitations,
  };
};